export interface LangMeta { code: string, name: string }

// This is an INTERNAL IT TEAM console, so its own admin UI is only Slovenian +
// English (not the full 24 EU locales the customer-facing ComplyIT app and the
// public signup pages ship — those live in libs/i18n/public).
export const LANGUAGES: LangMeta[] = [
    { code: 'sl', name: 'Slovenščina' },
    { code: 'en', name: 'English' },
]

export const ADMIN_LOCALES = LANGUAGES.map(l => l.code)

// isAdminLocale reports whether a locale is one the console UI actually ships.
export const isAdminLocale = (code: string | null | undefined): boolean =>
    !!code && ADMIN_LOCALES.includes(code)

// adminLocale clamps ANY locale to the console's own surface. Should a locale the
// console has no catalog for ever reach it (e.g. a "pt" left over from an older
// build, or a profile language), it renders in English rather than half-translated.
export const adminLocale = (code: string | null | undefined): string =>
    isAdminLocale(code) ? (code as string) : 'en'

// The two surfaces persist their language under SEPARATE keys. 1.26.0 let them
// share 'locale', so merely viewing /signup in English switched the whole console
// to English — the console UI is an internal, Slovenian-first tool and must not
// follow a customer-facing page's language (nor the other way round).
export const ADMIN_LOCALE_KEY  = 'locale'
export const PUBLIC_LOCALE_KEY = 'public_locale'

// One-time migration off the shared key. The stored value is handed to the public
// pages (that is where the 24 locales come from) and dropped from the console:
// under the shared key an admin choice is indistinguishable from a public-page
// one, so the console falls back to its default (Slovenian) and the operator
// re-picks English in the switcher if they actually want it.
const LOCALE_SPLIT_FLAG = 'locale_split_v1'

const migrateSharedLocaleKey = (): void => {
    if (localStorage.getItem(LOCALE_SPLIT_FLAG)) return
    localStorage.setItem(LOCALE_SPLIT_FLAG, '1')

    const shared = localStorage.getItem(ADMIN_LOCALE_KEY)
    if (!shared) return

    if (!localStorage.getItem(PUBLIC_LOCALE_KEY)) {
        localStorage.setItem(PUBLIC_LOCALE_KEY, shared)
    }

    localStorage.removeItem(ADMIN_LOCALE_KEY)
}

migrateSharedLocaleKey()

// resolveInitialLocale picks the console's startup locale: a previously chosen
// CONSOLE locale wins (clamped to sl/en), otherwise Slovenian — the console is an
// internal IT TEAM tool. A language chosen on the public pages is deliberately
// ignored here; it lives under PUBLIC_LOCALE_KEY.
export const resolveInitialLocale = (): string => {
    const stored = localStorage.getItem(ADMIN_LOCALE_KEY)
    if (stored) return adminLocale(stored)

    return 'sl'
}
